import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { memorySearch, signalStateHash } from "@/services/memorySearch";
import type { MemorySearchResponse, SignalStateHashResponse } from "@/types";

/**
 * Experiment memory search hook (R9 / Sub-3 + signal_state_hash
 * propagation follow-up shipped 2026-05-21).
 *
 * Source: docs/engineering/TASKS.md § ENG-R9-MEMORY-SEARCH Sub-3
 *         + signal_state_hash propagation follow-up.
 * Spec:   docs/product/experience-and-ui.md § Experiment Memory Search L1289-L1326
 *         ("Typing renders incremental results"); 300ms debounce
 *         chosen so a normal-pace typist (4-5 chars/sec) only fires
 *         one request per pause, not per keystroke.
 *
 * - Debounces the raw query at the spec-mandated 300ms.
 * - Fetches the per-app `signal_state_anchor.current_hash` via
 *   `GET /api/v1/memory/signal-state-hash/{app_id}` and threads it
 *   into the search request. On first-run apps the endpoint returns
 *   a deterministic bootstrap synthetic hash with `bootstrap=true`
 *   so the request always validates against the
 *   `MemorySearchRequest.signal_state_hash` min_length=1 contract.
 * - No cache invalidation on result clicks (the retrieval is
 *   stateless; clicks navigate away).
 * - `staleTime: 0` for the search query so a re-typed query re-hits
 *   the backend; the signal-state-hash query carries a longer
 *   `staleTime` since the hash flips only on the canonical 4 cache-
 *   invalidation events.
 */
const MEMORY_SEARCH_KEY_ROOT = "memory_search";
const SIGNAL_STATE_HASH_KEY_ROOT = "memory_search_signal_state_hash";
const DEBOUNCE_MS = 300;
const SIGNAL_STATE_HASH_STALE_MS = 30_000;

/**
 * Pre-resolution placeholder used between mount and the first
 * successful `signal_state_hash` fetch. The placeholder satisfies the
 * `MemorySearchRequest.signal_state_hash` min_length=1 contract so the
 * hook can opt-into firing a search even before the hash GET resolves
 * (typical case: the operator types fast). The hook prefers the real
 * hash whenever it is available; this constant is kept exported so
 * existing test fixtures + verify anchors continue to resolve.
 */
export const MEMORY_SEARCH_SIGNAL_STATE_PLACEHOLDER = "v1:placeholder";

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(handle);
  }, [value, delayMs]);
  return debounced;
}

/**
 * TanStack hook around the signal-state-hash GET endpoint. Exported so
 * future surfaces (recommendations / hypothesis cache / dashboard
 * coherence checks) can consume the same per-app hash without
 * duplicating the fetch. Disabled when `appId` is null.
 */
export function useSignalStateHash(appId: string | null) {
  return useQuery<SignalStateHashResponse, Error>({
    queryKey: [SIGNAL_STATE_HASH_KEY_ROOT, appId],
    queryFn: () => {
      if (!appId) throw new Error("appId required");
      return signalStateHash(appId);
    },
    enabled: Boolean(appId),
    staleTime: SIGNAL_STATE_HASH_STALE_MS,
  });
}

export interface UseMemorySearchOptions {
  appId: string | null;
  rawQuery: string;
  limit?: number;
  /**
   * True once the user opens the search. The signal-state-hash read waits
   * for it: the header search is on every route, and a read nobody uses on
   * page load costs every route a request (ENG-APP-UX-TUNEUP C8d — the bell's
   * Needs-you read took its place in the shell's 8). Defaults to true.
   */
  active?: boolean;
}

export function useMemorySearch(options: UseMemorySearchOptions) {
  const { appId, rawQuery, limit, active = true } = options;
  const debouncedQuery = useDebouncedValue(rawQuery.trim(), DEBOUNCE_MS);
  const hashQuery = useSignalStateHash(
    active || debouncedQuery.length > 0 ? appId : null,
  );
  const resolvedHash =
    hashQuery.data?.signal_state_hash ?? MEMORY_SEARCH_SIGNAL_STATE_PLACEHOLDER;
  const enabled = Boolean(appId) && debouncedQuery.length > 0;

  return useQuery<MemorySearchResponse, Error>({
    queryKey: [
      MEMORY_SEARCH_KEY_ROOT,
      appId,
      debouncedQuery,
      resolvedHash,
      limit ?? 10,
    ],
    queryFn: () => {
      if (!appId) throw new Error("appId required");
      return memorySearch({
        app_id: appId,
        raw_query: debouncedQuery,
        signal_state_hash: resolvedHash,
        limit,
      });
    },
    enabled,
    staleTime: 0,
  });
}
